import * as React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
    AlertCircle,
    CheckCircle2,
    Download,
    Loader2,
    RotateCw,
    Sparkles,
    Trash2,
    X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { AdvertiserOwnerBadge } from "@/components/reassign-advertiser-popover";
import {
    bannerDownloadUrl,
    deleteBanner,
    retryBanner,
    deleteBannerAsset,
    getBanner,
    listBanners,
    type BannerAssetDto,
    type BannerDto,
} from "@/api/banners";
import { apiErrorMessage } from "@/lib/api";
import { cn } from "@/lib/utils";

interface Props {
    pageId: number | null;
}

/**
 * Banners tab inside the constructor center pane. Loads every banner
 * attached to the draft's pageId. Banners are page-scoped, so a draft
 * that hasn't been published yet has no pageId and this panel shows the
 * "publish first" empty state.
 */
export function BannersPanel({ pageId }: Props): React.ReactNode {
    const [openBannerId, setOpenBannerId] = React.useState<number | null>(null);

    // Hook is always called; the queryFn only fires when enabled. Keeps the
    // hook order stable across "draft has pageId" transitions (publish flow).
    const bannersQuery = useQuery({
        queryKey: ["banners", "page", pageId ?? 0],
        queryFn: () => listBanners(pageId as number),
        enabled: pageId != null,
        refetchInterval: 8_000,
    });

    if (pageId == null) {
        return (
            <div className="flex h-full flex-col items-center justify-center gap-3 p-8 text-center text-sm text-muted-foreground">
                <Sparkles className="h-8 w-8 text-muted-foreground/60" />
                <div className="max-w-md">
                    Banners attach to a <strong>published page</strong>. Publish this draft first
                    (use the agent: <span className="font-mono">publish_draft</span>), then ask the
                    agent to build a banner pack — "make a Google Display banner pack" or "make a
                    Meta Feed banner using this hero image".
                </div>
            </div>
        );
    }

    if (bannersQuery.isLoading) {
        return <div className="flex h-full items-center justify-center"><Spinner /></div>;
    }
    if (bannersQuery.isError) {
        return <div className="flex h-full items-center justify-center text-sm text-destructive">Couldn't load banners.</div>;
    }
    const banners = bannersQuery.data || [];

    if (openBannerId != null) {
        return (
            <BannerDetail
                bannerId={openBannerId}
                onClose={() => setOpenBannerId(null)}
            />
        );
    }

    if (banners.length === 0) {
        return (
            <div className="flex h-full flex-col items-center justify-center gap-3 p-8 text-center text-sm text-muted-foreground">
                <Sparkles className="h-8 w-8 text-muted-foreground/60" />
                <div className="max-w-md">
                    No banners yet. Ask the agent in the chat — e.g. <em>"make a Google Display banner pack
                    for this page"</em> or <em>"create a Meta Feed banner using the hero image".</em> The
                    agent knows every platform's exact dims, text limits, and policy rules.
                </div>
            </div>
        );
    }

    // Group by platform
    const grouped = new Map<string, BannerDto[]>();
    for (const b of banners) {
        const arr = grouped.get(b.platform) || [];
        arr.push(b);
        grouped.set(b.platform, arr);
    }

    return (
        <div className="flex h-full flex-col overflow-y-auto p-4">
            <div className="mb-3 flex items-center justify-between">
                <div className="text-sm font-medium">
                    {banners.length} banner{banners.length === 1 ? "" : "s"} across {grouped.size} platform{grouped.size === 1 ? "" : "s"}
                </div>
            </div>
            <div className="space-y-6">
                {[...grouped.entries()].map(([platform, list]) => (
                    <section key={platform}>
                        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{platform}</h3>
                        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                            {list.map((b) => (
                                <BannerCard
                                    key={b.id}
                                    banner={b}
                                    onOpen={() => setOpenBannerId(b.id)}
                                />
                            ))}
                        </div>
                    </section>
                ))}
            </div>
        </div>
    );
}

function BannerCard({ banner, onOpen }: { banner: BannerDto; onOpen: () => void }): React.ReactNode {
    const queryClient = useQueryClient();
    const confirm = useConfirm();
    const deleteMut = useMutation({
        mutationFn: () => deleteBanner(banner.id),
        onSuccess: () => {
            toast.success(`Deleted "${banner.name}"`);
            queryClient.invalidateQueries({ queryKey: ["banners"] });
        },
        onError: (e) => toast.error(apiErrorMessage(e, "Couldn't delete")),
    });
    const retryMut = useMutation({
        mutationFn: () => retryBanner(banner.id),
        onSuccess: () => {
            toast.success("Retrying — re-checking this banner");
            queryClient.invalidateQueries({ queryKey: ["banners"] });
        },
        onError: (e) => toast.error(apiErrorMessage(e, "Retry failed")),
    });
    // A failed banner or one wedged mid-generation can be retried in place.
    const canRetry = banner.status === "failed" || banner.status === "generating" || banner.status === "validating";
    const previewAssets = (banner.assets || [])
        .filter((a) => a.kind === "image" || a.kind === "logo")
        .slice(0, 4);
    const statusColor =
        banner.status === "ready" ? "text-emerald-600 dark:text-emerald-400" :
        banner.status === "failed" ? "text-destructive" :
        "text-muted-foreground";
    return (
        <div
            className="group flex flex-col gap-2 rounded-lg border border-border bg-card p-3 transition-colors hover:bg-muted/30"
            data-testid={`banner-card-${banner.id}`}
        >
            <button
                type="button"
                onClick={onOpen}
                className="flex flex-1 flex-col items-start gap-2 text-left"
            >
                <div className="flex w-full items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium" title={banner.name}>{banner.name}</div>
                        <div className={cn("text-[10px]", statusColor)}>
                            {banner.status} · {banner.assetCount} asset{banner.assetCount === 1 ? "" : "s"}
                        </div>
                    </div>
                </div>
                {previewAssets.length > 0 && (
                    <div className="grid w-full grid-cols-4 gap-1">
                        {previewAssets.map((a) => (
                            <div key={a.id} className="aspect-square overflow-hidden rounded bg-muted/40">
                                <img src={a.url ?? ""} alt="" className="h-full w-full object-cover" />
                            </div>
                        ))}
                        {(banner.assetCount || 0) > previewAssets.length && (
                            <div className="flex aspect-square items-center justify-center rounded bg-muted/40 text-[10px] text-muted-foreground">
                                +{(banner.assetCount || 0) - previewAssets.length}
                            </div>
                        )}
                    </div>
                )}
            </button>
            <div className="flex items-center gap-1 border-t border-border/60 pt-2">
                <a
                    href={bannerDownloadUrl(banner.id)}
                    download
                    className="inline-flex items-center gap-1 rounded p-1 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
                    title="Download zip"
                    onClick={(e) => e.stopPropagation()}
                    data-testid={`banner-download-${banner.id}`}
                >
                    <Download className="h-3.5 w-3.5" /> zip
                </a>
                {canRetry && (
                    <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="h-7 gap-1 px-2 text-xs text-muted-foreground hover:text-foreground"
                        onClick={(e) => { e.stopPropagation(); retryMut.mutate(); }}
                        disabled={retryMut.isPending}
                        title="Re-check this banner. Recovers a failed one whose assets are fine, or un-sticks one wedged mid-generation."
                        data-testid={`banner-retry-${banner.id}`}
                    >
                        {retryMut.isPending ? <Spinner size="sm" /> : <RotateCw className="h-3.5 w-3.5" />}
                        Retry
                    </Button>
                )}
                <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="ml-auto h-7 px-2 text-xs text-muted-foreground hover:text-destructive"
                    onClick={(e) => {
                        e.stopPropagation();
                        confirm.show({
                            title: `Delete banner "${banner.name}"?`,
                            description: "Removes the banner + every asset, including R2 binaries.",
                            destructive: true,
                            confirmLabel: "Delete banner",
                            "data-testid": `banner-delete-confirm-${banner.id}`,
                            onConfirm: () => Promise.resolve(deleteMut.mutate()),
                        });
                    }}
                    data-testid={`banner-delete-${banner.id}`}
                >
                    <Trash2 className="h-3.5 w-3.5" />
                </Button>
            </div>
        </div>
    );
}

/**
 * Fullscreen detail: every asset at scale, text variants, last validation
 * result. Loaded fresh from the server (`getBanner`) so we always see the
 * latest after agent runs.
 */
function BannerDetail({ bannerId, onClose }: { bannerId: number; onClose: () => void }): React.ReactNode {
    const queryClient = useQueryClient();
    const confirm = useConfirm();
    const bannerQuery = useQuery({
        queryKey: ["banner", bannerId],
        queryFn: () => getBanner(bannerId),
        refetchInterval: 6_000,
    });

    const deleteAssetMut = useMutation({
        mutationFn: (assetId: number) => deleteBannerAsset(bannerId, assetId),
        onSuccess: () => {
            toast.success("Asset deleted");
            queryClient.invalidateQueries({ queryKey: ["banner", bannerId] });
            queryClient.invalidateQueries({ queryKey: ["banners"] });
        },
        onError: (e) => toast.error(apiErrorMessage(e, "Couldn't delete asset")),
    });

    if (bannerQuery.isLoading) {
        return <div className="flex h-full items-center justify-center"><Spinner /></div>;
    }
    if (bannerQuery.isError || !bannerQuery.data) {
        return (
            <div className="flex h-full flex-col p-4">
                <button onClick={onClose} className="mb-3 self-start text-sm text-muted-foreground hover:text-foreground">
                    ← Back
                </button>
                <div className="text-sm text-destructive">Couldn't load this banner.</div>
            </div>
        );
    }
    const b = bannerQuery.data;
    const imageAssets = b.assets.filter((a) => a.kind === "image" || a.kind === "logo");
    const textAssets = b.assets.filter((a) => a.kind === "text");

    return (
        <div className="flex h-full flex-col overflow-y-auto">
            <div className="sticky top-0 z-10 flex items-center gap-3 border-b border-border bg-background/95 px-4 py-2.5 backdrop-blur">
                <button onClick={onClose} className="rounded p-1 hover:bg-muted" data-testid="banner-detail-close">
                    <X className="h-4 w-4" />
                </button>
                <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                        <div className="truncate text-sm font-medium">{b.name}</div>
                        <AdvertiserOwnerBadge
                            entityType="banner"
                            entityId={b.id}
                            currentAdvertiserId={b.advertiserId ?? null}
                            compact
                            data-testid="banner-advertiser-badge"
                        />
                    </div>
                    <div className="text-[10px] text-muted-foreground">{b.platform} · {b.assetCount} assets · status: {b.status}</div>
                </div>
                <a
                    href={bannerDownloadUrl(b.id)}
                    download
                    className="inline-flex items-center gap-1 rounded-md border border-border bg-background px-3 py-1.5 text-xs hover:bg-muted"
                    data-testid="banner-detail-download"
                >
                    <Download className="h-3.5 w-3.5" /> Download zip
                </a>
            </div>
            <div className="space-y-6 p-4">
                {b.lastValidation && (
                    <ValidationSummary v={b.lastValidation} />
                )}
                {imageAssets.length > 0 && (
                    <section>
                        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Visual assets</h3>
                        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                            {imageAssets.map((a) => (
                                <AssetTile
                                    key={a.id}
                                    asset={a}
                                    onDelete={() => {
                                        confirm.show({
                                            title: "Delete this asset?",
                                            description: `${a.formatSlug} (variant ${a.variantIdx}). The agent can regenerate it later.`,
                                            destructive: true,
                                            confirmLabel: "Delete",
                                            onConfirm: () => Promise.resolve(deleteAssetMut.mutate(a.id)),
                                        });
                                    }}
                                />
                            ))}
                        </div>
                    </section>
                )}
                {textAssets.length > 0 && (
                    <section>
                        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Text variants</h3>
                        <div className="space-y-2">
                            {textAssets.map((a) => (
                                <div
                                    key={a.id}
                                    className="flex items-start gap-2 rounded-md border border-border bg-card p-2 text-sm"
                                    data-testid={`banner-text-${a.id}`}
                                >
                                    <div className="min-w-[140px] shrink-0">
                                        <div className="font-mono text-[11px] text-muted-foreground">{a.textSlot}</div>
                                        <div className="text-[10px] text-muted-foreground/70">variant {a.variantIdx} · {(a.content || "").length} chars</div>
                                    </div>
                                    <div className="flex-1 break-words text-foreground">{a.content}</div>
                                    <button
                                        type="button"
                                        className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-destructive"
                                        onClick={() => deleteAssetMut.mutate(a.id)}
                                        title="Delete"
                                    >
                                        <Trash2 className="h-3.5 w-3.5" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </section>
                )}
            </div>
        </div>
    );
}

function ValidationSummary({ v }: { v: import("@/api/banners").BannerValidationRunDto }): React.ReactNode {
    const errors = (v.issues || []).filter((i) => i.severity === "error");
    const warns = (v.issues || []).filter((i) => i.severity === "warn");
    const passed = v.passed === true;
    return (
        <section className={cn(
            "rounded-md border p-3",
            passed ? "border-emerald-400/40 bg-emerald-50/40 dark:bg-emerald-950/20"
                   : "border-destructive/40 bg-destructive/5",
        )}>
            <div className="mb-2 flex items-center gap-2 text-sm font-medium">
                {passed ? <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                        : <AlertCircle className="h-4 w-4 text-destructive" />}
                {passed ? "Validation passed" : "Validation failed"}
                <span className="text-xs font-normal text-muted-foreground">
                    {errors.length} error{errors.length === 1 ? "" : "s"} · {warns.length} warning{warns.length === 1 ? "" : "s"}
                </span>
            </div>
            {(errors.length > 0 || warns.length > 0) && (
                <ul className="space-y-1 text-xs">
                    {[...errors, ...warns].slice(0, 12).map((i, idx) => (
                        <li key={idx} className="flex items-start gap-1.5">
                            {i.severity === "error"
                                ? <AlertCircle className="mt-0.5 h-3 w-3 shrink-0 text-destructive" />
                                : <Loader2 className="mt-0.5 h-3 w-3 shrink-0 text-amber-500" />}
                            <span>
                                {i.formatSlug && <span className="font-mono text-muted-foreground">{i.formatSlug}: </span>}
                                {i.textSlot && <span className="font-mono text-muted-foreground">{i.textSlot}: </span>}
                                {i.message}
                            </span>
                        </li>
                    ))}
                </ul>
            )}
            {v.summary && <div className="mt-2 text-xs text-muted-foreground">{v.summary}</div>}
        </section>
    );
}

function AssetTile({ asset, onDelete }: { asset: BannerAssetDto; onDelete: () => void }): React.ReactNode {
    const stateColor =
        asset.validationState === "error" ? "border-destructive/40" :
        asset.validationState === "warn" ? "border-amber-400/40" :
        "border-border";
    return (
        <div
            className={cn("group relative flex flex-col gap-1 rounded-md border bg-card p-2", stateColor)}
            data-testid={`banner-asset-${asset.id}`}
        >
            <div className="aspect-video w-full overflow-hidden rounded bg-[#1f1f1f]">
                {asset.url && (
                    <img
                        src={asset.url}
                        alt={asset.formatSlug}
                        className="h-full w-full object-contain"
                    />
                )}
            </div>
            <div className="flex items-center justify-between text-[10px]">
                <span className="font-mono text-muted-foreground truncate">{asset.formatSlug}</span>
                <span className="shrink-0 text-muted-foreground/70">{asset.width}×{asset.height} · {asset.sizeBytes ? Math.round(asset.sizeBytes / 1024) + "KB" : "?"}</span>
            </div>
            {asset.validationMessage && (
                <div className="text-[10px] text-amber-600 dark:text-amber-400">{asset.validationMessage}</div>
            )}
            <button
                type="button"
                className="absolute right-1 top-1 hidden rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-destructive group-hover:block"
                onClick={onDelete}
                title="Delete asset"
            >
                <Trash2 className="h-3.5 w-3.5" />
            </button>
        </div>
    );
}
